import { useForm } from 'react-hook-form';
import { toJSONSchema } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import type { ZodType } from 'zod';
import type { DefaultValues, FieldValues, UseFormReturn } from 'react-hook-form';

export interface UseFormProps<T> {
  formSchema: ZodType;
  formDefaultValues: T;
  schemaTitle?: string;
  mode?: 'onBlur' | 'onChange' | 'onSubmit' | 'onTouched' | 'all';
  values?: T;
  reValidateMode?: 'onChange' | 'onBlur' | 'onSubmit';
  shouldFocusError?: boolean;
}

export type ExtendedUseFormReturn<T extends FieldValues> = UseFormReturn<T> & {
  formProperties: Record<string, unknown>;
  formMethods: UseFormReturn<T>;
};

const useFormHook = <T extends FieldValues>({
  formSchema,
  formDefaultValues,
  mode = 'onTouched',
  values,
  reValidateMode = 'onChange',
  shouldFocusError = true,
}: UseFormProps<T>): ExtendedUseFormReturn<T> => {
  const formMethods = useForm({
    mode,
    // @ts-expect-error zodResolver infers from schema type; this hook accepts any schema dynamically
    resolver: zodResolver(formSchema),
    reValidateMode: reValidateMode,
    shouldFocusError,
    defaultValues: formDefaultValues as DefaultValues<T>,
    values,
  });

  const jsonSchema = toJSONSchema(formSchema);
  const formProperties = jsonSchema.properties ?? {};

  useEffect(() => {
    const handleFocusOut = (e: FocusEvent): void => {
      const elementId = (e.target as HTMLElement)?.id;
      if (formProperties && elementId in formProperties) {
        // TODO - add analytics send to mixpanel per input on blur
      }
    };

    window.addEventListener('focusout', handleFocusOut);

    return () => {
      window.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  // @ts-expect-error formMethods is UseFormReturn<FieldValues> but consumers expect UseFormReturn<T>
  return { ...formMethods, formProperties, formMethods };
};

export default useFormHook;
